import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatMoney } from '@/lib/credit';
import type { ZifekUser } from '@/hooks/useAuth';

/** Page publique de paiement FeexPay (règlements ponctuels). */
const FEEXPAY_PAY_PAGE = 'https://page.feexpay.me/kNwo/zifek';

type StatusKind = 'paid' | 'pending' | 'failed' | 'other';
type SourceKey = 'log' | 'subscription' | 'order';

interface PaymentEntry {
  key: string;
  source: SourceKey;
  title: string;
  reference: string;
  amount: number;
  currency: string;
  rawStatus: string;
  kind: StatusKind;
  provider: string;
  operator?: string;
  phone?: string;
  country?: string;
  date: string;
  error?: string;
}

interface LogRow {
  id: number;
  provider: string | null;
  flow: string | null;
  status: string | null;
  amount: number | string | null;
  currency: string | null;
  operator: string | null;
  country: string | null;
  phone: string | null;
  reference: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string | null;
}

interface SubscriptionRow {
  id: number;
  amount: number | string | null;
  currency: string | null;
  method: string | null;
  period_months: number | null;
  status: string | null;
  reference: string | null;
  created_at: string | null;
}

interface OrderRow {
  id: number;
  titre: string | null;
  date_time: string | null;
  totalttc: string | null;
  sp: string | null;
  monaie: string | null;
  methodepayment: string | null;
  statut_com_client: string | null;
  statut_com_vendeur: string | null;
  code: string | null;
}

interface Props {
  user: ZifekUser;
}

function classify(raw: string | null | undefined): StatusKind {
  const s = (raw || '').toLowerCase();
  if (/(success|succes|succès|paid|paye|payé|confirme|confirm|reussi|réussi|complete)/.test(s)) return 'paid';
  if (/(fail|error|declin|refus|annul|cancel|expire|reject)/.test(s)) return 'failed';
  if (/(pending|attente|processing|initiat|cours|created|essai|trial)/.test(s)) return 'pending';
  return 'other';
}

const KIND_LABEL: Record<StatusKind, string> = {
  paid: 'Payé',
  pending: 'En attente',
  failed: 'Échoué',
  other: 'Reçu',
};

const KIND_STYLE: Record<StatusKind, string> = {
  paid: 'bg-primary-100 text-primary-700',
  pending: 'bg-accent-100 text-accent-800',
  failed: 'bg-red-50 text-red-600',
  other: 'bg-background-200/70 text-foreground-600',
};

const SOURCE_META: Record<SourceKey, { label: string; icon: string; tint: string }> = {
  log: { label: 'Paiement en ligne', icon: 'ri-secure-payment-line', tint: 'bg-primary-100 text-primary-700' },
  subscription: { label: 'Abonnement Zifek', icon: 'ri-vip-crown-line', tint: 'bg-accent-100 text-accent-800' },
  order: { label: 'Achat boutique', icon: 'ri-shopping-bag-3-line', tint: 'bg-secondary-100 text-secondary-700' },
};

function fmtDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function fmtTime(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function toAmount(value: number | string | null | undefined): number {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? '0'));
  return Number.isFinite(n) ? n : 0;
}

export default function PaymentsTab({ user }: Props) {
  const [entries, setEntries] = useState<PaymentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | StatusKind>('all');
  const [search, setSearch] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const currency = user.monaie || 'MAD';

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const uid = String(user.id);

      // Rapprochement des commandes : id de compte, email ou téléphone.
      const filters: string[] = [`user_id.eq.${uid}`];
      if (user.email) {
        filters.push(`email.eq.${user.email}`);
        filters.push(`user_id.eq.${user.email}`);
      }
      if (user.telephone) {
        filters.push(`tel.eq.${user.telephone}`);
        filters.push(`user_id.eq.${user.telephone}`);
      }

      const [logsRes, subsRes, ordersRes] = await Promise.all([
        supabase
          .from('payment_attempt_logs')
          .select('id, provider, flow, status, amount, currency, operator, country, phone, reference, error_code, error_message, created_at')
          .eq('user_id', uid)
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('zifek_payments')
          .select('id, amount, currency, method, period_months, status, reference, created_at')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('commande')
          .select('id, titre, date_time, totalttc, sp, monaie, methodepayment, statut_com_client, statut_com_vendeur, code')
          .or(filters.join(','))
          .order('date_time', { ascending: false })
          .limit(100),
      ]);

      if (logsRes.error && subsRes.error && ordersRes.error) {
        throw new Error('load failed');
      }

      const merged: PaymentEntry[] = [];

      ((logsRes.data as LogRow[]) || []).forEach((row) => {
        merged.push({
          key: `log-${row.id}`,
          source: 'log',
          title: row.flow ? `Paiement ${row.flow}` : 'Paiement en ligne',
          reference: row.reference || `#${row.id}`,
          amount: toAmount(row.amount),
          currency: row.currency || currency,
          rawStatus: row.status || '',
          kind: classify(row.status),
          provider: row.provider || 'FeexPay',
          operator: row.operator || undefined,
          phone: row.phone || undefined,
          country: row.country || undefined,
          date: row.created_at || '',
          error: row.error_message || row.error_code || undefined,
        });
      });

      ((subsRes.data as SubscriptionRow[]) || []).forEach((row) => {
        const months = row.period_months || 0;
        merged.push({
          key: `sub-${row.id}`,
          source: 'subscription',
          title: months > 0 ? `Abonnement Zifek · ${months} mois` : 'Abonnement Zifek',
          reference: row.reference || `#${row.id}`,
          amount: toAmount(row.amount),
          currency: row.currency || currency,
          rawStatus: row.status || '',
          kind: classify(row.status),
          provider: row.method || 'Zifek',
          date: row.created_at || '',
        });
      });

      ((ordersRes.data as OrderRow[]) || []).forEach((row) => {
        const amount = toAmount(row.totalttc ?? row.sp);
        merged.push({
          key: `order-${row.id}`,
          source: 'order',
          title: row.titre || `Commande #${row.id}`,
          reference: row.code || `#${row.id}`,
          amount,
          currency: row.monaie || currency,
          rawStatus: row.statut_com_client || row.statut_com_vendeur || '',
          kind: classify(row.statut_com_client || row.statut_com_vendeur),
          provider: row.methodepayment || 'Sur place / livraison',
          date: row.date_time || '',
        });
      });

      merged.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
      setEntries(merged);
    } catch {
      setError('Impossible de charger votre historique de paiements.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [user, currency]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const paid = entries.filter((e) => e.kind === 'paid');
    const pending = entries.filter((e) => e.kind === 'pending').length;
    const failed = entries.filter((e) => e.kind === 'failed').length;
    const total = paid.reduce((sum, e) => sum + e.amount, 0);
    return { total, paidCount: paid.length, pending, failed };
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (filter !== 'all' && e.kind !== filter) return false;
      if (!q) return true;
      return (
        e.title.toLowerCase().includes(q)
        || e.reference.toLowerCase().includes(q)
        || e.provider.toLowerCase().includes(q)
        || (e.operator || '').toLowerCase().includes(q)
      );
    });
  }, [entries, filter, search]);

  const copyReference = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      /* presse-papiers indisponible */
    }
  };

  const filters: { id: 'all' | StatusKind; label: string; count: number }[] = [
    { id: 'all', label: 'Tous', count: entries.length },
    { id: 'paid', label: 'Réussis', count: stats.paidCount },
    { id: 'pending', label: 'En attente', count: stats.pending },
    { id: 'failed', label: 'Échoués', count: stats.failed },
  ];

  return (
    <div className="max-w-4xl xl:max-w-5xl mx-auto space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            Historique des paiements
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Toutes vos transactions : achats boutique, paiements en ligne et abonnements.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-background-300/70 rounded-full text-xs font-semibold text-foreground-700 whitespace-nowrap cursor-pointer hover:bg-background-100 disabled:opacity-40 transition-colors"
        >
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {/* Résumé */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-background-50 border border-background-200/40 rounded-xl p-4">
          <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center mb-3">
            <i className="ri-wallet-3-line text-lg"></i>
          </div>
          <p className="text-lg md:text-xl font-bold font-heading text-foreground-950 truncate">
            {formatMoney(stats.total, currency)}
          </p>
          <p className="text-xs text-foreground-500 mt-0.5">Total réglé</p>
        </div>
        <div className="bg-background-50 border border-background-200/40 rounded-xl p-4">
          <div className="w-9 h-9 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center mb-3">
            <i className="ri-checkbox-circle-line text-lg"></i>
          </div>
          <p className="text-lg md:text-xl font-bold font-heading text-foreground-950">{stats.paidCount}</p>
          <p className="text-xs text-foreground-500 mt-0.5">Paiements réussis</p>
        </div>
        <div className="bg-background-50 border border-background-200/40 rounded-xl p-4">
          <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center mb-3">
            <i className="ri-time-line text-lg"></i>
          </div>
          <p className="text-lg md:text-xl font-bold font-heading text-foreground-950">{stats.pending}</p>
          <p className="text-xs text-foreground-500 mt-0.5">En attente</p>
        </div>
        <div className="bg-background-50 border border-background-200/40 rounded-xl p-4">
          <div className="w-9 h-9 rounded-lg bg-background-200/80 text-foreground-700 flex items-center justify-center mb-3">
            <i className="ri-close-circle-line text-lg"></i>
          </div>
          <p className="text-lg md:text-xl font-bold font-heading text-foreground-950">{stats.failed}</p>
          <p className="text-xs text-foreground-500 mt-0.5">Échoués</p>
        </div>
      </div>

      {/* Filtres + recherche */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="inline-flex items-center gap-1 p-1 bg-background-100 border border-background-200/50 rounded-full overflow-x-auto">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                filter === f.id
                  ? 'bg-foreground-900 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              {f.label}
              <span className={`ml-1.5 ${filter === f.id ? 'text-background-50/70' : 'text-foreground-400'}`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 md:max-w-xs">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Référence, opérateur, libellé..."
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/50 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 transition-colors"
          />
        </div>
      </div>

      {/* Liste */}
      <div className="bg-background-50 border border-background-200/40 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
            <p className="text-xs text-foreground-400 mt-3">Chargement de vos paiements...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-3">
              <i className="ri-error-warning-line text-xl text-accent-800"></i>
            </div>
            <p className="text-sm font-medium text-foreground-800">{error}</p>
            <button
              onClick={load}
              className="mt-4 px-4 py-2 bg-foreground-900 text-background-50 rounded-full text-xs font-semibold cursor-pointer hover:bg-foreground-800 transition-colors"
            >
              Réessayer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-receipt-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-sm font-medium text-foreground-800">
              {entries.length === 0 ? 'Aucun paiement pour le moment' : 'Aucun résultat'}
            </p>
            <p className="text-xs text-foreground-400 mt-1 max-w-sm">
              {entries.length === 0
                ? 'Dès que vous réglerez une commande ou un abonnement, la transaction apparaîtra ici avec sa référence.'
                : 'Essayez un autre filtre ou une autre recherche.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/40">
            {filtered.map((e) => {
              const meta = SOURCE_META[e.source];
              const open = openKey === e.key;
              return (
                <div key={e.key}>
                  <button
                    onClick={() => setOpenKey(open ? null : e.key)}
                    className="w-full flex items-center gap-3 px-4 md:px-5 py-3.5 text-left cursor-pointer hover:bg-background-100/40 transition-colors"
                  >
                    <span className={`w-9 h-9 rounded-lg ${meta.tint} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${meta.icon} text-base`}></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-foreground-900 truncate">{e.title}</span>
                      <span className="block text-[11px] text-foreground-500 truncate">
                        {fmtDate(e.date)} {fmtTime(e.date) && `· ${fmtTime(e.date)}`} · {e.provider}
                      </span>
                    </span>
                    <span className="text-right flex-shrink-0">
                      <span className="block text-sm font-bold text-foreground-950 whitespace-nowrap">
                        {formatMoney(e.amount, e.currency)}
                      </span>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${KIND_STYLE[e.kind]}`}>
                        {KIND_LABEL[e.kind]}
                      </span>
                    </span>
                    <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 text-lg flex-shrink-0`}></i>
                  </button>

                  {open && (
                    <div className="px-4 md:px-5 pb-4 pt-1 bg-background-100/30">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-background-50 border border-background-200/40 rounded-lg p-4">
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Référence</p>
                          <div className="flex items-center gap-2 min-w-0">
                            <p className="text-xs font-medium text-foreground-800 truncate">{e.reference}</p>
                            <button
                              onClick={() => copyReference(e.reference)}
                              title="Copier la référence"
                              className="w-6 h-6 rounded-md text-foreground-500 hover:text-foreground-900 hover:bg-background-100 flex items-center justify-center cursor-pointer flex-shrink-0"
                            >
                              <i className={`${copied === e.reference ? 'ri-check-line text-primary-600' : 'ri-file-copy-line'} text-xs`}></i>
                            </button>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Type</p>
                          <p className="text-xs font-medium text-foreground-800">{meta.label}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Moyen de paiement</p>
                          <p className="text-xs font-medium text-foreground-800 truncate">{e.provider}</p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Statut</p>
                          <p className="text-xs font-medium text-foreground-800">
                            {KIND_LABEL[e.kind]}{e.rawStatus ? ` · ${e.rawStatus}` : ''}
                          </p>
                        </div>
                        {e.operator && (
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Opérateur</p>
                            <p className="text-xs font-medium text-foreground-800 truncate">{e.operator}</p>
                          </div>
                        )}
                        {e.phone && (
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Numéro</p>
                            <p className="text-xs font-medium text-foreground-800 truncate">{e.phone}</p>
                          </div>
                        )}
                        {e.country && (
                          <div className="min-w-0">
                            <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Pays</p>
                            <p className="text-xs font-medium text-foreground-800">{e.country}</p>
                          </div>
                        )}
                        {e.error && (
                          <div className="min-w-0 sm:col-span-2">
                            <p className="text-[10px] font-semibold text-foreground-400 tracking-[0.08em] uppercase mb-1">Détail</p>
                            <p className="text-xs text-red-600">{e.error}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Régler un paiement ponctuel */}
      <div className="bg-background-50 border border-background-200/40 rounded-xl p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-11 h-11 rounded-lg bg-accent-100 text-accent-800 flex items-center justify-center flex-shrink-0">
          <i className="ri-bank-card-line text-lg"></i>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground-900">Régler un paiement</p>
          <p className="text-xs text-foreground-500 mt-0.5">
            Payer par Mobile Money, carte bancaire ou wallet sur notre page de paiement sécurisée.
          </p>
        </div>
        <a
          href={FEEXPAY_PAY_PAGE}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          Payer maintenant
          <i className="ri-external-link-line text-sm"></i>
        </a>
      </div>

      <p className="text-[11px] text-foreground-400 flex items-center gap-1.5">
        <i className="ri-shield-check-line"></i>
        Les paiements sont traités par FeexPay. Conservez la référence pour tout suivi auprès de votre commerçant.
      </p>
    </div>
  );
}